/**
 * Who's online in the current team workspace (Phoenix.Presence, sync Phase 5).
 * Fed by useWorkspaceRealtime from presence_state/presence_diff channel
 * events; cleared on channel leave. `rawState` keeps the full phoenix
 * Presence shape (`{ [userId]: { metas: [...] } }`) so request-level
 * filtering (selectRequestViewers) can inspect every meta, not just the
 * first; `members` stays the flattened one-entry-per-user list for the
 * coarse "who's online" case.
 */
import { create } from 'zustand';

export const usePresenceStore = create((set) => ({
  rawState: {},
  members: [],
  setPresenceState: (rawState) => set({ rawState, members: presenceToMembers(rawState) }),
  clearPresence: () => set({ rawState: {}, members: [] }),
}));

/** phoenix Presence state object -> flat member list. */
export function presenceToMembers(state) {
  return Object.entries(state || {}).map(([userId, entry]) => {
    const meta = entry?.metas?.[0] || {};
    return {
      userId,
      name: meta.name || `User ${userId}`,
      clientId: meta.client_id || null,
      onlineAt: meta.online_at || null,
      // Carried through, not consumed here: no channel tracks a `status` meta
      // today (chat_channel.ex has no presence:context handler at all), so this
      // is null everywhere. It exists so that when the backend does start
      // sending it, the avatar status badge picks it up with no client change —
      // flattening it away here would be the one place that silently drops it.
      status: meta.status || null,
    };
  });
}

/** Members of the workspace roster whose metas show them viewing `requestId`, excluding `excludeUserId`. */
export function selectRequestViewers(rawState, requestId, excludeUserId) {
  if (requestId == null) return [];
  const out = [];
  Object.entries(rawState || {}).forEach(([userId, entry]) => {
    if (String(userId) === String(excludeUserId)) return;
    const metas = entry?.metas || [];
    const onRequest = metas.some(
      (m) => m.request_id != null && String(m.request_id) === String(requestId)
    );
    if (!onRequest) return;
    const meta = metas[0] || {};
    out.push({ userId, name: meta.name || `User ${userId}` });
  });
  return out;
}
